import { HubConnection, HubConnectionBuilder } from '@aspnet/signalr';
import SettingsService from 'phoenix/services/settingsService';

import { EventHandler } from './event';
import { Subscription } from './subscription';
import { Action, IDictionary } from 'util/domain';
import { CommonUtilsService } from 'util/services';
import { AuthService } from 'phoenix/services';

export interface EventRelaySubscription {
	subscriptionId: number;
	joinMethod: string;
	eventMethod: string;
	leaveMethod: string;
}

export class EventRelayService {
	public static readonly $inject = [
		'$q',
		'settingsService',
		'pxAuthService',
		'utils'
	];

	private readonly connectionBuilder: HubConnectionBuilder;
	private readonly connectionLostHandlers: IDictionary<Action> = {};
	private connection: HubConnection;

	constructor(
		private readonly $q:ng.IQService,
		settings: SettingsService,
		pxAuthService: AuthService,
		private readonly utils: CommonUtilsService
		) {
		this.connectionBuilder = new HubConnectionBuilder()
			.withUrl(settings.eventRelayPath, { accessTokenFactory: () => pxAuthService.token.value });
	}

	/**
	 * Subscribe to a single event from the event relay service by event type.
	 * Returns a subscription object that can be used to unsubscribe from the event
	 * at a later time.
	 * 
	 * @param type The event type to subscribe to
	 * @param subscription The subscription data from a successful subscribe API call
	 * @param handler Event handler for the subscription
	 */
	public subscribe<T>(type: string, subscription: EventRelaySubscription, handler: EventHandler<T>): ng.IPromise<Subscription<T>> {
		if (!this.connection) {
			this.connection = this.connectionBuilder.build();
			this.connection.onclose(err => {
				Object.keys(this.connectionLostHandlers).forEach(key => {
					this.connectionLostHandlers[key]();
				})
			});
		}
		
		return this.toNgPromise(this.connection.start()
			.then(() => this.connection.invoke(subscription.joinMethod, subscription.subscriptionId))
			.then(() => new Subscription<T>(this.$q,
				{
					connection: this.connection,
					type,
					subscriptionId: subscription.subscriptionId,
					eventMethod: subscription.eventMethod,
					leaveMethod: subscription.leaveMethod,
					handler
				})));
	}

	private toNgPromise<T>(promise: Promise<T>): ng.IPromise<T> {
		return this.$q<T>((resolve, reject) => {
			promise
				.then((subscription) => resolve(subscription))
				.catch((err) => reject(err))
		});
	}

	public onConnectionLost(handler: Action): Action {
		const token = this.utils.generateUid();
		const unsubscribe = () => {
			if (!(token in this.connectionLostHandlers)) {
				return;
			}

			this.connectionLostHandlers[token] = null;
			delete this.connectionLostHandlers[token];
		};

		this.connectionLostHandlers[token] = () => {
			unsubscribe();
			handler();
		};

		return unsubscribe;
	}

	public disconnect() {
		if (this.connection) {
			return this.connection.stop()
				// Null out closed connection so next call to subscribe creates a new connection
				.then(() => {
					this.connection = null;
					Object.keys(this.connectionLostHandlers).forEach(key => {
						this.connectionLostHandlers[key] = null;
						delete this.connectionLostHandlers[key];
					})
				});
		} else {
			return this.$q.resolve();
		}
	}
}


// WEBPACK FOOTER //
// ./src/phoenix/services/event-relay/event-relay.service.ts